import { Dispatch, ReactNode, SetStateAction } from "react";
import { Editor, EditorState, RichUtils, CompositeDecorator, ContentState, ContentBlock } from "draft-js";
import "draft-js/dist/Draft.css";
import { Divider, IconButton, Stack } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  LinkOff, // Using LinkOff from Material-UI icons
} from "@mui/icons-material";

// Assuming Frame is a custom component, ensure its path is correct
// import Frame from "@/components/Frame"; // Commented out as I don't have access to this path

interface Style {
  icon: ReactNode;
  label: string;
  style?: string;
  block?: string;
}

const STYLES: Style[] = [
  {
    icon: <FormatBold />,
    label: "Negrita",
    style: "BOLD",
  },
  {
    icon: <FormatItalic />,
    label: "Inclinada",
    style: "ITALIC",
  },
  {
    icon: <FormatListBulleted />,
    label: "Viñetas",
    block: "unordered-list-item",
  },
  {
    icon: <FormatListNumbered />,
    label: "Lista Ordenada",
    block: "ordered-list-item",
  },
];

const Link = (props: any) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "#006cb7",
        textDecoration: "underline",
        cursor: "pointer",
      }}
    >
      {props.children}
    </a>
  );
};

const decorator = new CompositeDecorator([
  {
    strategy: (contentBlock: ContentBlock, callback: (start: number, end: number) => void, contentState: ContentState) => {
      contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
      }, callback);
    },
    component: Link,
  },
]);

interface WysiwygEditorProps {
  value: EditorState;
  onChange: Dispatch<SetStateAction<EditorState>>;
}

const WysiwygEditor = ({ value, onChange }: WysiwygEditorProps) => {
  const applyStyle = (style: string) => {
    onChange(RichUtils.toggleInlineStyle(value, style));
  };

  const applyBlock = (block: string) => {
    onChange(RichUtils.toggleBlockType(value, block));
  };

  const isActive = (style: string) => {
    const currentStyle = value.getCurrentInlineStyle();
    return currentStyle.has(style);
  };

  const addLink = (editorState: EditorState, url: string) => {
    const contentState = editorState.getCurrentContent();
    // Create an entity for the link with the URL data
    const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", { url });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    // Apply the entity to the current selection
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    return (RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey), {decorator});
  };

  const removeLink = (editorState: EditorState) => {
    // To remove a link, toggleLink with a null entityKey
    return RichUtils.toggleLink(editorState, editorState.getSelection(), null);
  };

  const handleAddLink = () => {
    const selection = value.getSelection();
    
    if (selection.isCollapsed()) {
      // If no text is selected, prompt for text and URL
      const linkText = prompt("Enter the text for the link:");
      if (!linkText) return; // User cancelled or entered empty text

      const url = prompt("Enter the URL:");
      if (!url) return; // User cancelled or entered empty URL

      const currentContent = value.getCurrentContent();
      const contentStateWithEntity = currentContent.createEntity("LINK", "MUTABLE", { url });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      // Insert the link text and apply the entity
      const newContentState = ContentState.replaceText(
        currentContent,
        selection,
        linkText,
        value.getCurrentInlineStyle(),
        entityKey
      );

      const newEditorState = EditorState.push(value, newContentState, 'insert-characters');
      onChange(RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey));

    } else {
      // If text is selected, just prompt for URL
      const url = prompt("Enter the URL");
      if (url) {
        const newEditorState = addLink(value, url);
        onChange(newEditorState);
      }
    }
  };

  const handleRemoveLink = () => {
    const newEditorState = removeLink(value);
    onChange(newEditorState);
  };

  return (
    // Replaced Frame with a simple div for demonstration.
    // If you have a Frame component, uncomment and use it.
    <div style={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" }}>
      <Stack direction="row" spacing={2} px={1} py={1} sx={{ borderBottom: "1px solid #eee" }}>
        {STYLES.map((style) => {
          const isActiveStyle = style.style && isActive(style.style);
          const isActiveBlock = style.block && value.getCurrentContent().getBlockForKey(value.getSelection().getStartKey()).getType() === style.block;
          return (
            <IconButton
              key={style.label}
              size="small"
              color={isActiveStyle || isActiveBlock ? "primary" : "default"}
              onClick={() => (style.style ? applyStyle(style.style) : applyBlock(style.block!))}
            >
              {style.icon}
            </IconButton>
          );
        })}
        <IconButton size="small" color="default" onClick={handleAddLink}>
          <LinkIcon />
        </IconButton>
        <IconButton size="small" color="default" onClick={handleRemoveLink}>
          <LinkOff /> {/* Changed from Unlink to LinkOff */}
        </IconButton>
      </Stack>
      <Divider />
      <Stack px={2} py={1} sx={{ minHeight: 100, cursor: "text" }}>

        <Editor editorState={value} onChange={onChange} />
      </Stack>
    </div>
  );
};

export default WysiwygEditor;
